import api, { getSialApiHost } from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import msgbox, { notify, ask } from '../../versa/msgbox.js';
import filter from '../../versa/filter.js';
import view from '../views/movements.js';
import form from '../forms/transactions.js';
import move_location_bulk_form from '../forms/move-location-bulk.js';
import movement_control_form from '../forms/movement-control-report.js';
import * as types from '../types.js';
import { formatTransactionEndpointDisplay } from '../utils/format-transaction-endpoint.js';
import {
	formatMoveLocationConflictMessage,
	moveLocationConfirmFlags,
} from '../utils/stock-move-confirm.js';

function downloadBase64(base64Content, filename, mime)
{
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: mime || 'application/octet-stream' });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'download';
	a.click();
	URL.revokeObjectURL(a.href);
}

function escapeHtml(s)
{
	return String(s ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function escapeCsvCell(value)
{
	if (value == null || value === '')
		return '';
	const s = String(value);
	if (/[";\r\n]/.test(s))
		return '"' + s.replace(/"/g, '""') + '"';
	return s;
}

/** CSV separado por ; (compatível com Excel PT-BR). */
function toCsvSemicolon(rows, headers)
{
	const line = (cells) => cells.map(escapeCsvCell).join(';');
	const h = line(headers.map(h => h.label));
	const body = rows.map((row) =>
		line(headers.map(h =>
		{
			const v = row[h.field];
			if (v != null && typeof v === 'object')
				return JSON.stringify(v);
			return v;
		})));
	return [h, ...body].join('\r\n');
}

function downloadText(text, filename, mime)
{
	const blob = new Blob(['\ufeff' + text], { type: mime });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename;
	a.click();
	URL.revokeObjectURL(a.href);
}

/** Mesma resolução de rótulo que a grid (versa/grid.js `key`). */
async function keyForExport(store, keys, labels = 'name', separator = ' ')
{
	if (!keys || (Array.isArray(keys) && (keys.length === 0 || keys.some((v) => v == null || v === ''))))
		return '';
	const entry = await db.get(store, keys);
	if (!entry)
		return '';
	return (Array.isArray(labels) ? labels : [labels]).map((label) => entry[label]).join(separator);
}

function formatDateTimeBr(v)
{
	if (v == null || v === '')
		return '';
	const d = new Date(v);
	if (isNaN(d.getTime()))
		return String(v);
	const pad = (n) => String(n).padStart(2, '0');
	return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatNumberBr(value, minFrac, maxFrac)
{
	if (value == null || value === '')
		return '';
	const n = Number(value);
	if (isNaN(n))
		return String(value);
	return n.toLocaleString('pt-BR', { minimumFractionDigits: minFrac, maximumFractionDigits: maxFrac });
}

const _txTypeOpt = types.opt_transaction_type();
const _mkOpt = types.opt_movement_kind();

function transactionTypeLabel(c)
{
	return _txTypeOpt.options.find((x) => x.value === c)?.label || c || '';
}

function movementKindLabel(k)
{
	if (k == null || k === '')
		return '';
	return _mkOpt.options.find((x) => x.value === k)?.label || k;
}

/**
 * Uma linha de movimentação com os mesmos textos da tela (nomes, tipos em PT, endereços, números pt-BR).
 */
async function enrichMovementRowForExport(r)
{
	const [company, product, userName, lotRec, origem, destino] = await Promise.all([
		keyForExport('companies', r.company_id, 'nick'),
		keyForExport('products', [r.company_id, r.product_id], 'name'),
		keyForExport('users', r.user_id, 'name'),
		db.get('lots', [r.company_id, r.lot_id]),
		formatTransactionEndpointDisplay(keyForExport, r.company_id, r.source_type, r.source),
		formatTransactionEndpointDisplay(keyForExport, r.company_id, r.target_type, r.target),
	]);
	return {
		...r,
		_company: company || '',
		_sku: r.sku || '',
		_product: product || '',
		_user: userName || '',
		_lot_fab: lotRec && lotRec.lot_number != null ? String(lotRec.lot_number) : '',
		_position: r.location_address || '',
		_origem_fmt: origem,
		_destino_fmt: destino,
		_date_br: formatDateTimeBr(r.date_time),
		_source_t: transactionTypeLabel(r.source_type),
		_target_t: transactionTypeLabel(r.target_type),
		_q_br: formatNumberBr(r.quantity, 0, 2),
		_v_br: formatNumberBr(r.value, 2, 2),
		_mk: movementKindLabel(r.movement_kind),
	};
}

async function enrichMovementsForExportAll(rows, onProgress = null)
{
	const out = [];
	const chunk = 50;
	for (let i = 0; i < rows.length; i += chunk)
	{
		const slice = rows.slice(i, i + chunk);
		const part = await Promise.all(slice.map((r) => enrichMovementRowForExport(r)));
		out.push(...part);
		if (onProgress)
			onProgress(out.length, rows.length);
	}
	return out;
}

function normAddressForFilter(s)
{
	return String(s ?? '').replace(/[\s\-]+/g, '').toLowerCase();
}

/** Id da posição: número ou endereço (ex.: S-1 / s-1); usa o mesmo critério de filtro `address` na API. */
async function resolveLocationIdForMove(companyId, warehouseId, raw)
{
	const rawStr = String(raw ?? '').trim();
	if (!rawStr)
		return NaN;
	const n = Number(rawStr);
	if (Number.isInteger(n) && n >= 1)
		return n;
	const term = normAddressForFilter(rawStr);
	if (!term)
		return NaN;
	const wh = Number(warehouseId);
	if (!companyId || !Number.isInteger(wh) || wh < 1)
		return NaN;
	const filter = `company_id*eq*${companyId},warehouse_id*eq*${wh},address*co*${term}`;
	const q = `?filter=${encodeURIComponent(filter)}&limit=80&sort=4`;
	const rows = await api.call(api.list_locations, q);
	if (!Array.isArray(rows) || rows.length === 0)
		return NaN;
	if (rows.length === 1)
		return Number(rows[0].id);
	const want = normAddressForFilter(rawStr);
	for (const r of rows)
	{
		const addr = Array.isArray(r.address) ? r.address.join('-') : String(r.address ?? '');
		if (normAddressForFilter(addr) === want)
			return Number(r.id);
	}
	return NaN;
}

const movements = function()
{
	let movements_crud = crud.create({
		default_columns: '1,2,18,3,4,16,5,6,7,8,9,10,11,12,13',
		async loadBaseData()
		{
			await this.loadLightBaseCatalogs();
			api.call(api.list_users, this.queryAll()).then((data) => db.set('users', data).catch(error => notify(error)));
			await this.loadScopedLocationsToDb();
		},
		async editFilter()
		{
			await this.loadScopedLocationsToDb(this.filter);
			filter.edit(this.applyFilter.bind(this), this.filter, {
				reloadLocations: async (formData) => { await this.loadScopedLocationsToDb(formData); },
			});
		},
		async applyFilter(data)
		{
			this.filter = data;
			this.filterExplicitlyApplied = true;
			await this.loadScopedLocationsToDb(data);
			await this.loadPage(1);
		},
		async queryData(query)
		{
			return await api.call(api.list_transactions, query);
		},
		async loadRecord(id)
		{
			return await api.call(api.select_transaction, ...JSON.parse(id));
		},
		async saveRecord(data, old_data)
		{
			let _record = {
				product_id: Number(data.product_id),
				lot_id: Number(data.lot_id),
				source_type: data.source_type,
				target_type: data.target_type,
				source: JSON.parse(data.source),
				target: JSON.parse(data.target),
				quantity: Number(data.quantity),
				value: Number(data.value),
			};
			if (old_data)
				_record.movement_kind = data.movement_kind || null;
			else if (data.movement_kind)
				_record.movement_kind = data.movement_kind;
			if (old_data)
				return await api.call(api.update_transaction, data.company_id, data.id, _record);
			else
				return await api.call(api.insert_transaction, data.company_id, _record);
		},
		async _exportMovementsDelimited(filename, title)
		{
			const rows = await this.queryFilteredData();
			if (!rows.length)
			{
				notify('Nenhum registro no filtro.', 'Exportar');
				return;
			}
			const mb = msgbox({ animation: true, text: 'Preparando exportação…' });
			let mapped;
			try
			{
				mapped = await enrichMovementsForExportAll(rows);
			}
			finally
			{
				mb.destroy();
			}
			const headers = [
				{ field: '_company', label: 'Empresa' },
				{ field: 'id', label: 'Transação' },
				{ field: '_sku', label: 'SKU' },
				{ field: '_product', label: 'Produto' },
				{ field: 'lot_id', label: 'Lote (id interno)' },
				{ field: '_lot_fab', label: 'Lote nº' },
				{ field: '_position', label: 'Posição' },
				{ field: '_user', label: 'Usuário' },
				{ field: '_date_br', label: 'Data/Hora' },
				{ field: '_source_t', label: 'Origem Tipo' },
				{ field: '_target_t', label: 'Destino Tipo' },
				{ field: '_origem_fmt', label: 'Origem' },
				{ field: '_destino_fmt', label: 'Destino' },
				{ field: '_q_br', label: 'Quantidade' },
				{ field: '_mk', label: 'Natureza' },
				{ field: '_v_br', label: 'Valor' },
			];
			const csv = toCsvSemicolon(mapped, headers);
			downloadText(csv, filename, 'text/csv;charset=utf-8');
			notify('Download iniciado.', title);
		},
		async exportMovementsCsv()
		{
			ask(`Exportar todos os registros do filtro atual em CSV (separador ;)?`, 'Exportar CSV', async (answer) =>
			{
				if (answer !== 'Y')
					return;
				await this._exportMovementsDelimited(
					`movimentacoes_${new Date().toISOString().slice(0, 10)}.csv`,
					'CSV'
				);
			});
		},
		async exportMovementsExcel()
		{
			ask(`Exportar registros do filtro para Excel (arquivo .xls com separador ;)?`, 'Exportar Excel', async (answer) =>
			{
				if (answer !== 'Y')
					return;
				await this._exportMovementsDelimited(
					`movimentacoes_${new Date().toISOString().slice(0, 10)}.xls`,
					'Excel'
				);
			});
		},
		async printMovementsPdf()
		{
			const rows = await this.queryFilteredData();
			if (!rows.length)
			{
				notify('Nenhum registro no filtro.', 'Imprimir');
				return;
			}
			const w = window.open('', '_blank');
			if (!w)
			{
				notify('Permita pop-ups para gerar o PDF.', 'Imprimir');
				return;
			}
			const slice = rows.slice(0, 2000);
			const mb = msgbox({ animation: true, text: 'Preparando visualização…' });
			let enriched;
			try
			{
				enriched = await enrichMovementsForExportAll(slice);
			}
			finally
			{
				mb.destroy();
			}
			const h = (s) =>
			{
				if (s == null || s === '')
					return '';
				return String(s)
					.replace(/&/g, '&amp;')
					.replace(/</g, '&lt;')
					.replace(/>/g, '&gt;')
					.replace(/"/g, '&quot;');
			};
			let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Movimentações</title>
			<style>
				body { font-family: Arial, sans-serif; margin: 1.2rem; }
				h1 { font-size: 1.1rem; }
				table { border-collapse: collapse; width: 100%; font-size: 0.65rem; }
				th, td { border: 1px solid #444; padding: 0.2rem 0.35rem; text-align: left; vertical-align: top; }
				th { background: #eee; }
			</style></head><body><h1>Movimentações (filtrado)</h1><table><thead><tr>
			<th>Empresa</th><th>Transação</th><th>SKU</th><th>Produto</th><th>Lote (id)</th><th>Lote nº</th><th>Posição</th><th>Usuário</th>
			<th>Data/Hora</th><th>Origem Tipo</th><th>Destino Tipo</th><th>Origem</th><th>Destino</th>
			<th>Quantidade</th><th>Natureza</th><th>Valor</th>
			</tr></thead><tbody>`;
			for (const r of enriched)
			{
				html += '<tr>';
				html += `<td>${h(r._company)}</td>`;
				html += `<td>${h(r.id)}</td>`;
				html += `<td>${h(r._sku)}</td>`;
				html += `<td>${h(r._product)}</td>`;
				html += `<td>${h(r.lot_id)}</td>`;
				html += `<td>${h(r._lot_fab)}</td>`;
				html += `<td>${h(r._position)}</td>`;
				html += `<td>${h(r._user)}</td>`;
				html += `<td>${h(r._date_br)}</td>`;
				html += `<td>${h(r._source_t)}</td>`;
				html += `<td>${h(r._target_t)}</td>`;
				html += `<td>${h(r._origem_fmt)}</td>`;
				html += `<td>${h(r._destino_fmt)}</td>`;
				html += `<td>${h(r._q_br)}</td>`;
				html += `<td>${h(r._mk)}</td>`;
				html += `<td>${h(r._v_br)}</td>`;
				html += '</tr>';
			}
			html += '</tbody></table>';
			if (rows.length > 2000)
				html += `<p>… e mais ${rows.length - 2000} linhas (limite de impressão). Use exportação CSV completa.</p>`;
			html += '<script>window.onload=function(){window.print();}<\/script></body></html>';
			w.document.write(html);
			w.document.close();
		},
		moveLocationBulk()
		{
			const crud = this;
			let fs;
			const sendRequest = async (data) =>
			{
				const co = data.warehouse_company_id;
				if (!co)
				{
					notify('Selecione a empresa.', 'Mover posição inteira');
					return;
				}
				const sw = Number(data.source_warehouse_id);
				const tw = Number(data.target_warehouse_id);
				if (!Number.isInteger(sw) || sw < 1 || !Number.isInteger(tw) || tw < 1)
				{
					notify('Selecione armazém de origem e destino na lista (após escolher a empresa).', 'Mover posição inteira');
					return;
				}
				let sl = Number(data.source_location_id);
				let tl = Number(data.target_location_id);
				if (!Number.isInteger(sl) || sl < 1)
					sl = await resolveLocationIdForMove(co, sw, data.source_location_id);
				if (!Number.isInteger(tl) || tl < 1)
					tl = await resolveLocationIdForMove(co, tw, data.target_location_id);
				if (!Number.isInteger(sl) || sl < 1)
				{
					notify('Posição de origem inválida ou ambígua. Use a lista (endereço após o armazém) ou o id numérico da posição.', 'Mover posição inteira');
					return;
				}
				if (!Number.isInteger(tl) || tl < 1)
				{
					notify('Posição de destino inválida ou ambígua. Use a lista (endereço após o armazém) ou o id numérico da posição.', 'Mover posição inteira');
					return;
				}
				const payload = {
					target_warehouse_id: tw,
					target_location_id: tl,
					reason: (data.reason || '').trim() || undefined,
				};
				if (data.movement_kind && String(data.movement_kind).trim())
					payload.movement_kind = String(data.movement_kind).trim();
				const url = `${getSialApiHost()}/companies/${co}/warehouses/${sw}/locations/${sl}/move-all-available`;
				const finishMoveOk = (out) =>
				{
					const n = out.lines_moved ?? 0;
					const q = out.quantity_total ?? 0;
					const qb = out.quantity_blocked_moved ?? 0;
					const blockedTxt = qb > 0 ? ` (${qb} bloqueada(s))` : '';
					notify(
						n
							? `Movimentadas ${n} transação(ões), ${q} unidade(s)${blockedTxt}.`
							: 'Nenhuma linha com saldo livre ou bloqueado na posição de origem.',
						'Mover posição inteira'
					);
					fs.destroy();
					crud.loadPage();
				};
				const mb = msgbox({ animation: true });
				let res;
				try
				{
					res = await fetch(url, {
						method: 'POST',
						body: JSON.stringify(payload),
						credentials: 'include',
						headers: { 'Content-Type': 'application/json' }
					});
				}
				finally
				{
					mb.destroy();
				}
				if (res.status === 409)
				{
					let j;
					try
					{
						j = await res.json();
					}
					catch (e)
					{
						notify('Resposta inválida do servidor (409).', 'Mover posição inteira');
						return;
					}
					if (j && j.error === 'move_location_reserve_conflict')
					{
						const { msg } = formatMoveLocationConflictMessage(j);
						const flags = moveLocationConfirmFlags(j);
						if (!flags.canProceed)
						{
							notify(msg, 'Mover posição inteira');
							return;
						}
						ask(msg, 'Mover posição inteira', async (answer) =>
						{
							if (answer !== 'Y')
								return;
							const mb2 = msgbox({ animation: true });
							let res2;
							try
							{
								res2 = await fetch(url, {
									method: 'POST',
									body: JSON.stringify({
										...payload,
										move_free_only: flags.move_free_only,
										include_blocked: flags.include_blocked,
									}),
									credentials: 'include',
									headers: { 'Content-Type': 'application/json' }
								});
							}
							finally
							{
								mb2.destroy();
							}
							if (!res2.ok)
							{
								const t = await res2.text();
								notify(t || `${res2.status}`, 'Mover posição inteira');
								return;
							}
							const out2 = await res2.json();
							finishMoveOk(out2);
						});
						return;
					}
					notify(j.message || 'Conflito na movimentação.', 'Mover posição inteira');
					return;
				}
				if (!res.ok)
				{
					const t = await res.text();
					notify(t || `${res.status}`, 'Mover posição inteira');
					return;
				}
				const out = await res.json();
				finishMoveOk(out);
			};
			fs = formset.create(move_location_bulk_form, sendRequest);
			fs.init();
		},
		async deleteMovement()
		{
			const ids = this.getSelection();
			if (!ids.length)
			{
				notify('Selecione ao menos uma movimentação.', 'Excluir');
				return;
			}
			ask(`Excluir ${ids.length} movimentação(ões)?`, 'Confirmar', async (answer) =>
			{
				if (answer !== 'Y')
					return;
				for (const raw of ids)
				{
					const [company_id, id] = JSON.parse(raw);
					const r = await api.call(api.delete_transaction, company_id, id);
					if (r === undefined)
						return;
				}
				notify('Registro(s) excluído(s).', 'Excluir');
				this.clearGridSelection();
				this.loadPage();
			});
		},

		movementControl()
		{
			const fs = formset.create(movement_control_form, async (data) =>
			{
				const root = fs.form?.node || document.querySelector('#movement-control-form');
				const types = [];
				if (root?.querySelector('#type_ll')?.checked) types.push('LL');
				if (root?.querySelector('#type_lr')?.checked) types.push('LR');
				if (root?.querySelector('#type_ro')?.checked) types.push('RO');
				const format = (root?.querySelector('#format')?.value || data.format || 'json').toLowerCase();
				const view = (root?.querySelector('#view')?.value || data.view || 'moves').toLowerCase();
				const isMoves = view === 'moves';
				const unitLabel = isMoves ? 'movimento(s)' : 'posição(ões)';

				const companyId = data.company_id;
				if (!companyId) {
					notify('Selecione a empresa.', 'Atenção');
					return false;
				}
				const dateFrom = data.date_from;
				const dateTo = data.date_to;
				if (!dateFrom || !dateTo) {
					notify('Informe o período (De e Até).', 'Atenção');
					return false;
				}
				if (dateTo < dateFrom) {
					notify('A data "Até" deve ser igual ou posterior à data "De".', 'Atenção');
					return false;
				}
				if (!types.length) {
					notify('Marque ao menos um tipo de movimento (L→L, L→R ou R→O).', 'Atenção');
					return false;
				}
				if (typeof api.movement_control_report !== 'function') {
					notify('Frontend desatualizado: recarregue a página com Ctrl+F5.', 'Atenção');
					return false;
				}
				const params = {
					format,
					view,
					date_from: dateFrom,
					date_to: dateTo,
					types: types.join(','),
					warehouse_id: data.warehouse_id || undefined,
				};
				fs.destroy();
				try {
					if (format === 'json') {
						params.limit = 2000;
						params.offset = 0;
						const resp = await api.movement_control_report(companyId, params);
						if (!resp.ok) {
							const err = await resp.json().catch(() => ({}));
							notify(err?.error || err?.message || `Erro HTTP ${resp.status}`, 'Erro');
							return;
						}
						const payload = await resp.json();
						const rows = payload?.rows || [];
						if (!rows.length) {
							notify(
								isMoves
									? 'Nenhuma movimentação neste período e filtros.'
									: 'Nenhuma posição com movimentação neste período e filtros.',
								'Resultado',
							);
							return;
						}
						const legendHtml = (payload.legend || [])
							.filter((x) => (payload.types || []).includes(x.key))
							.map((x) => `<li><strong>${escapeHtml(x.code)}</strong> — ${escapeHtml(x.label)}</li>`)
							.join('');
						const viewTitle = isMoves ? 'Origem × Destino' : 'Por posição';
						let headersHtml;
						let filtersHtml;
						let tbody;
						if (isMoves) {
							headersHtml = `
								<th>Data/Hora</th><th>Tipo</th>
								<th>Posição origem</th><th>Posição destino</th>
								<th class="tar">Qtd</th><th>SKU</th><th>Produto</th>`;
							filtersHtml = [0, 1, 2, 3, 4, 5, 6].map((i) =>
								`<th><input type="search" data-col="${i}" placeholder="Filtrar…" aria-label="Filtrar coluna ${i}"></th>`
							).join('');
							tbody = rows.map((r) => {
								const cells = [
									r.data_hora || '—',
									r.tipo || '—',
									r.posicao_origem || '—',
									r.posicao_destino || '—',
									String(r.quantidade ?? ''),
									r.sku || '—',
									r.produto || '—',
								];
								const dataAttrs = cells.map((c, i) => `data-c${i}="${escapeHtml(String(c).toLowerCase())}"`).join(' ');
								return `<tr class="mc-row" ${dataAttrs}>
									<td class="tac">${escapeHtml(cells[0])}</td>
									<td class="tac">${escapeHtml(cells[1])}</td>
									<td>${escapeHtml(cells[2])}</td>
									<td>${escapeHtml(cells[3])}</td>
									<td class="tar">${escapeHtml(cells[4])}</td>
									<td>${escapeHtml(cells[5])}</td>
									<td>${escapeHtml(cells[6])}</td>
								</tr>`;
							}).join('');
						} else {
							headersHtml = `
								<th>Armazém</th><th>Posição</th>
								<th class="tar">Mov.</th><th class="tac">Último</th>
								<th>Tipos</th><th class="tar">SKUs</th><th class="tar">Saldo</th>`;
							filtersHtml = [0, 1, 2, 3, 4, 5, 6].map((i) =>
								`<th><input type="search" data-col="${i}" placeholder="Filtrar…" aria-label="Filtrar coluna ${i}"></th>`
							).join('');
							tbody = rows.map((r) => {
								const pos = r.posicao || r.endereco || '—';
								const cells = [
									r.armazem || '—',
									pos,
									String(r.qtd_movimentos ?? ''),
									r.ultimo_movimento || '—',
									r.tipos || '—',
									String(r.skus_distintos ?? ''),
									String(r.saldo_atual ?? ''),
								];
								const dataAttrs = cells.map((c, i) => `data-c${i}="${escapeHtml(String(c).toLowerCase())}"`).join(' ');
								return `<tr class="mc-row" ${dataAttrs}>
									<td>${escapeHtml(cells[0])}</td>
									<td>${escapeHtml(cells[1])}</td>
									<td class="tar">${escapeHtml(cells[2])}</td>
									<td class="tac">${escapeHtml(cells[3])}</td>
									<td>${escapeHtml(cells[4])}</td>
									<td class="tar">${escapeHtml(cells[5])}</td>
									<td class="tar">${escapeHtml(cells[6])}</td>
								</tr>`;
							}).join('');
						}
						const total = rows.length;
						const aviso = total >= 2000
							? `<p class="fss" style="color:var(--color-muted);margin:0.4rem 0 0">Exibindo até 2000 linhas. Exporte CSV/Excel/PDF para o conjunto completo.</p>`
							: '';
						const html = `
							<div class="msgbox mc-dialog" style="max-width:96vw;width:1100px">
								<style>
									.mc-dialog .mc-wrap { max-height:62vh; overflow:auto; border:1px solid #cbd5e1; border-radius:4px; }
									.mc-dialog table.mc-tbl { width:100%; min-width:900px; border-collapse:collapse; font-size:0.8rem; }
									.mc-dialog table.mc-tbl thead th {
										position:sticky; top:0; z-index:2;
										background:#1e3a5f; color:#fff; font-weight:600;
										padding:6px 8px; border:1px solid #16304f; white-space:nowrap;
									}
									.mc-dialog table.mc-tbl thead tr.mc-filters th {
										top:2.1rem; background:#f1f5f9; z-index:2; padding:4px 6px;
									}
									.mc-dialog table.mc-tbl thead tr.mc-filters input {
										width:100%; box-sizing:border-box; font-size:0.75rem;
										padding:3px 5px; border:1px solid #94a3b8; border-radius:3px;
									}
									.mc-dialog table.mc-tbl td {
										padding:5px 8px; border:1px solid #e2e8f0; vertical-align:middle;
									}
									.mc-dialog table.mc-tbl tbody tr:nth-child(even) td { background:#f8fafc; }
									.mc-dialog table.mc-tbl tbody tr:hover td { background:#e0f2fe; }
									.mc-dialog table.mc-tbl .tar { text-align:right; }
									.mc-dialog table.mc-tbl .tac { text-align:center; }
									.mc-dialog .mc-legend { margin:0.4rem 0 0.6rem 1.1rem; color:var(--color-muted); }
									.mc-dialog .mc-count { font-size:0.8rem; color:#475569; margin:0.35rem 0 0; }
								</style>
								<div class="mb-title">Controle de movimentações (${escapeHtml(viewTitle)}) — ${escapeHtml(dateFrom)} a ${escapeHtml(dateTo)}</div>
								<ul class="fss mc-legend">${legendHtml}</ul>
								${aviso}
								<div class="mc-wrap">
									<table class="mc-tbl" id="mc-table">
										<thead>
											<tr>${headersHtml}</tr>
											<tr class="mc-filters">${filtersHtml}</tr>
										</thead>
										<tbody>${tbody}</tbody>
									</table>
								</div>
								<p class="mc-count" id="mc-count">Exibindo ${total} de ${total} ${unitLabel}</p>
								<div class="fsb mtm">
									<button type="button" class="button" id="btn-mc-csv">CSV</button>
									<button type="button" class="button" id="btn-mc-xlsx">Excel</button>
									<button type="button" class="button" id="btn-mc-pdf">PDF</button>
									<button type="button" class="button bn_N">Fechar</button>
								</div>
							</div>`;
						const { default: dialog } = await import('../../versa/dialog.js');
						const dlg = dialog.create(html, (key) => {
							if (key === 'N' || key === 'Escape') dlg.destroy();
						}, /N|Escape/);
						const dlgRoot = dlg.getNode();
						const applyFilters = () => {
							const filters = [...dlgRoot.querySelectorAll('.mc-filters input')].map((inp) =>
								(inp.value || '').trim().toLowerCase()
							);
							let visible = 0;
							dlgRoot.querySelectorAll('#mc-table tbody tr.mc-row').forEach((tr) => {
								const ok = filters.every((f, i) => {
									if (!f) return true;
									return (tr.getAttribute(`data-c${i}`) || '').includes(f);
								});
								tr.style.display = ok ? '' : 'none';
								if (ok) visible += 1;
							});
							const el = dlgRoot.querySelector('#mc-count');
							if (el) el.textContent = `Exibindo ${visible} de ${total} ${unitLabel}`;
						};
						dlgRoot.querySelectorAll('.mc-filters input').forEach((inp) => {
							inp.addEventListener('input', applyFilters);
						});
						const exportFmt = async (fmt) => {
							try {
								const r2 = await api.movement_control_report(companyId, { ...params, format: fmt });
								if (!r2.ok) {
									const err2 = await r2.json().catch(() => ({}));
									notify(err2?.error || err2?.message || `Erro HTTP ${r2.status}`, 'Erro');
									return;
								}
								const result = await r2.json();
								if (fmt === 'csv' && result?.csv) {
									downloadBase64(result.csv, result.filename || 'controle-movimentacoes.csv', 'text/csv;charset=utf-8');
									notify('Download CSV iniciado.', 'Sucesso');
								} else if (fmt === 'xlsx' && result?.xlsx) {
									downloadBase64(result.xlsx, result.filename || 'controle-movimentacoes.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
									notify('Download Excel iniciado.', 'Sucesso');
								} else if (fmt === 'pdf' && result?.pdf) {
									downloadBase64(result.pdf, result.filename || 'controle-movimentacoes.pdf', 'application/pdf');
									notify('Download PDF iniciado.', 'Sucesso');
								} else {
									notify('Nenhum dado para exportar.', 'Aviso');
								}
							} catch (e) {
								notify(e?.message || String(e), 'Erro');
							}
						};
						dlgRoot.querySelector('#btn-mc-csv')?.addEventListener('click', () => exportFmt('csv'));
						dlgRoot.querySelector('#btn-mc-xlsx')?.addEventListener('click', () => exportFmt('xlsx'));
						dlgRoot.querySelector('#btn-mc-pdf')?.addEventListener('click', () => exportFmt('pdf'));
						return;
					}

					const resp = await api.movement_control_report(companyId, params);
					if (!resp.ok) {
						const err = await resp.json().catch(() => ({}));
						notify(err?.error || err?.message || `Erro HTTP ${resp.status}`, 'Erro');
						return;
					}
					const result = await resp.json();
					if (format === 'csv' && result?.csv) {
						downloadBase64(result.csv, result.filename || 'controle-movimentacoes.csv', 'text/csv;charset=utf-8');
						notify(`CSV gerado (${result.rows ?? ''} ${unitLabel}).`, 'Sucesso');
					} else if (format === 'xlsx' && result?.xlsx) {
						downloadBase64(result.xlsx, result.filename || 'controle-movimentacoes.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
						notify(`Excel gerado (${result.rows ?? ''} ${unitLabel}).`, 'Sucesso');
					} else if (format === 'pdf' && result?.pdf) {
						downloadBase64(result.pdf, result.filename || 'controle-movimentacoes.pdf', 'application/pdf');
						notify(`PDF gerado (${result.rows ?? ''} ${unitLabel}).`, 'Sucesso');
					} else {
						notify('Nenhum dado para exportar.', 'Aviso');
					}
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
			fs.init();
		},
	});

	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Movimentação', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador' },
		{ index: 18, field: 'sku', label: 'SKU', types: [types.name()], placeholder: 'trecho do SKU', title: 'Código SKU do produto (operador Contém recomendado)' },
		{ index: 3, field: 'product_id', label: 'Produto', types: [types.datalist()], placeholder: '', title: 'Produto', store: 'products', labels: ['sku', 'name'] },
		{ index: 4, field: 'lot_id', label: 'Lote', types: [types.number(0, 2)], placeholder: '0', title: 'Lote' },
		{ index: 15, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém (escopo do filtro de posição)', store: 'warehouses', labels: ['name'] },
		{ index: 16, field: 'location_id', label: 'Posição', types: [types.datalist(), types.name()], placeholder: '1-A-1-1', title: 'Posição no armazém (lista ou texto parcial)', store: 'locations', labels: ['address'], separator: '-' },
		{ index: 5, field: 'user_id', label: 'Usuário', types: [types.datalist()], placeholder: '', title: 'Usuário', store: 'users', labels: ['name'] },
		{ index: 6, field: 'date_time', label: 'Data/Hora', types: [types.datetime()], placeholder: '', title: 'Data e hora' },
		{ index: 7, field: 'source_type', label: 'Tipo Origem', types: [types.opt_transaction_type(), types.datalist()], placeholder: '', title: 'Tipo de origem' },
		{ index: 8, field: 'target_type', label: 'Tipo Destino', types: [types.opt_transaction_type(), types.datalist()], placeholder: '', title: 'Tipo de destino' },
		{ index: 9, field: 'source', label: 'Origem', types: [], placeholder: '', title: 'Origem' },
		{ index: 10, field: 'target', label: 'Destino', types: [], placeholder: '', title: 'Destino' },
		{ index: 11, field: 'quantity', label: 'Quantidade', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade' },
		{ index: 12, field: 'movement_kind', label: 'Natureza', types: [types.opt_movement_kind()], placeholder: '', title: 'MOV / RESUP / INV / BLOQ / UNPICK' },
		{ index: 13, field: 'value', label: 'Valor', types: [types.number(2)], placeholder: '0,00', title: 'Valor' },
	];

	let operations = [
		{ hash: '#move-location-bulk', label: 'Mover&nbsp;posição&nbsp;inteira' },
		{ hash: '#delete-movements', label: 'Excluir' },
	];
	let reports = [
		{ hash: '#export-movements-csv', label: 'Exportar&nbsp;CSV' },
		{ hash: '#export-movements-excel', label: 'Exportar&nbsp;Excel' },
		{ hash: '#print-movements-pdf', label: 'Imprimir&nbsp;(PDF)' },
		{ hash: '#movement-control', label: 'Controle&nbsp;de&nbsp;movimentações' },
	];

	router.set('#move-location-bulk', movements_crud.moveLocationBulk.bind(movements_crud));
	router.set('#delete-movements', movements_crud.deleteMovement.bind(movements_crud));
	router.set('#export-movements-csv', movements_crud.exportMovementsCsv.bind(movements_crud));
	router.set('#export-movements-excel', movements_crud.exportMovementsExcel.bind(movements_crud));
	router.set('#print-movements-pdf', movements_crud.printMovementsPdf.bind(movements_crud));
	router.set('#movement-control', movements_crud.movementControl.bind(movements_crud));

	movements_crud.run(
		'movements',
		'Movimentações',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports
	);
};

export default movements;
